import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Database, BrainCircuit, KanbanSquare, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const Expertise = () => {
  const { t } = useT();

  const expertises = [
    { icon: ShieldCheck, key: "cyber", title: t("expertises.cyber.title") },
    { icon: Database, key: "bigdata", title: t("expertises.bigdata.title") },
    { icon: BrainCircuit, key: "ai", title: t("expertises.ai.title") },
    { icon: KanbanSquare, key: "pm", title: t("expertises.pm.title") },
  ];

  return (
    <div data-testid="expertise-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hx-dot-grid opacity-50" />
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#1093f0]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-12">
          <div className="text-xs mono uppercase tracking-[0.3em] text-[#1093f0] mb-4">{t("expertisePage.kicker")}</div>
          <h1 className="text-5xl lg:text-7xl font-semibold text-[#131517] leading-[1.02] max-w-4xl">
            {t("expertisePage.title1")} <span className="italic font-normal">{t("expertisePage.title2")}</span>
          </h1>
          <p className="mt-8 text-lg text-[#3f444b] max-w-2xl">{t("expertisePage.desc")}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 space-y-24">
        {expertises.map(({ icon: Icon, key, title }, i) => {
          const items = t(`expertisePage.${key}.items`) || [];
          return (
            <div
              key={key}
              className="grid lg:grid-cols-12 gap-12 items-start"
              data-testid={`expertise-detail-${key}`}
            >
              <div className="lg:col-span-5">
                <div className="sticky top-32">
                  <div className="w-16 h-16 rounded-2xl bg-[#131517] flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-[#1093f0]" />
                  </div>
                  <h2 className="text-4xl lg:text-5xl font-semibold text-[#131517] mb-4">{title}</h2>
                  <p className="text-lg italic text-[#1093f0]">{t(`expertisePage.${key}.tagline`)}</p>
                </div>
              </div>
              <div className="lg:col-span-7">
                <p className="text-lg text-[#3f444b] leading-relaxed mb-10">{t(`expertisePage.${key}.desc`)}</p>
                <ul className="space-y-3">
                  {Array.isArray(items) && items.map((it) => (
                    <li key={it} className="flex items-start gap-3 py-4 border-b border-black/8 last:border-0">
                      <CheckCircle2 className="w-5 h-5 text-[#1093f0] mt-0.5 shrink-0" />
                      <span className="text-[#131517]">{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-28">
        <div className="relative overflow-hidden rounded-3xl bg-[#f4f5f6] p-12 lg:p-16 flex flex-col lg:flex-row items-center justify-between gap-8">
          <div>
            <h3 className="text-3xl lg:text-4xl font-semibold text-[#131517] max-w-xl">
              {t("expertisePage.ctaTitle")}
            </h3>
            <p className="mt-4 text-[#3f444b]">{t("expertisePage.ctaDesc")}</p>
          </div>
          <Link
            to="/contact"
            className="hx-btn-primary inline-flex items-center gap-2 bg-[#1f2226] text-white px-7 py-4 rounded-full text-sm font-medium"
            data-testid="expertise-page-cta"
          >
            {t("expertisePage.ctaButton")} <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Expertise;
